<template>
  <!-- Mobile / tablet bottom navigation. Hidden on large screens (≥ 992px) via
       the scoped media query; App_Shell renders it at the end of every page. -->
  <nav class="m-bottomnav" aria-label="Main navigation">
    <template v-for="item in navItems" :key="item.label">
      <!-- "More" opens the bottom sheet instead of navigating. -->
      <button
        v-if="item.isMore"
        type="button"
        class="m-bottomnav__item"
        :class="{ 'is-active': showMoreSheet || moreIsActive }"
        :aria-expanded="showMoreSheet ? 'true' : 'false'"
        @click="openMoreSheet"
      >
        <span class="m-bottomnav__icon">
          <img :src="item.icon" alt="" :class="item.iconClass" aria-hidden="true" />
          <span v-if="badgeOf(item)" class="tk-badge m-bottomnav__badge">{{ badgeLabel(badgeOf(item)) }}</span>
        </span>
        <span class="m-bottomnav__label">{{ item.label }}</span>
      </button>
      <router-link
        v-else
        :to="{ name: item.route }"
        class="m-bottomnav__item"
        :class="{ 'is-active': isActive(item) }"
        :aria-current="isActive(item) ? 'page' : null"
      >
        <span class="m-bottomnav__icon">
          <img :src="item.icon" alt="" :class="item.iconClass" aria-hidden="true" />
          <span v-if="badgeOf(item)" class="tk-badge m-bottomnav__badge">{{ badgeLabel(badgeOf(item)) }}</span>
        </span>
        <span class="m-bottomnav__label">{{ item.label }}</span>
      </router-link>
    </template>
  </nav>

  <!-- "More" bottom sheet — slides up from the bottom edge over the nav. -->
  <transition name="sheet">
    <div
      v-if="showMoreSheet"
      class="m-sheet-overlay"
      @click.self="closeMoreSheet"
    >
      <div class="m-sheet" role="dialog" aria-modal="true" aria-label="More options">
        <span class="m-sheet__handle" @click="closeMoreSheet"></span>
        <div class="m-sheet__header">
          <div class="m-sheet__account">
            <span class="m-sheet__avatar">{{ initials }}</span>
            <div class="m-sheet__account-meta">
              <span class="m-sheet__account-name">{{ displayName }}</span>
              <span v-if="account && account.email" class="m-sheet__account-email">{{ account.email }}</span>
            </div>
          </div>
          <button type="button" class="m-sheet__close" aria-label="Close" @click="closeMoreSheet">
            <font-awesome-icon icon="fa-solid fa-xmark" />
          </button>
        </div>
        <ul class="m-sheet__list">
          <li v-for="link in moreItems" :key="link.label">
            <router-link
              v-if="link.route"
              :to="{ name: link.route }"
              class="m-sheet__item"
              :class="{ 'is-active': isActive(link) }"
              @click="closeMoreSheet"
            >
              <span class="m-sheet__icon">
                <img :src="link.icon" alt="" aria-hidden="true" />
              </span>
              <span class="m-sheet__label">{{ link.label }}</span>
            </router-link>
            <span v-else class="m-sheet__item is-disabled" aria-disabled="true">
              <span class="m-sheet__icon">
                <img :src="link.icon" alt="" aria-hidden="true" />
              </span>
              <span class="m-sheet__label">{{ link.label }}</span>
              <span class="m-sheet__badge">Soon</span>
            </span>
          </li>
          <li class="m-sheet__signout-row">
            <button type="button" class="m-sheet__item m-sheet__item--danger" @click="logout">
              <span class="m-sheet__icon">
                <img :src="logoutIcon" alt="" aria-hidden="true" />
              </span>
              <span class="m-sheet__label">Signout</span>
            </button>
          </li>
        </ul>
      </div>
    </div>
  </transition>
</template>

<script>
import {
  DASHBOARD_ROUTE,
  ORGANIZATIONS_ROUTE,
  NEW_ORGANIZATION_ROUTE,
  EDIT_ORGANIZATION_ROUTE,
  ORGANIZATION_DETAILS_ROUTE,
  ORGANIZATION_SCREENS_ROUTE,
  NEW_SCREEN_ROUTE,
  ORGANIZATION_USERS_ROUTE,
  TICKETS_ROUTE,
  CHAT_ROUTE,
  KODE_AI_ROUTE,
} from '@/router/routes';
import { LOGOUT } from '@/store/action_types';
import { chatUnread } from '@/utils/chat_notifications';
import { accountDisplayName, accountInitials } from '@/utils/display';

import { library } from '@fortawesome/fontawesome-svg-core';
import { faXmark } from '@fortawesome/free-solid-svg-icons';

// Reuse the exact icon assets the sidenav uses so the two navs stay in sync.
import dashboardIcon from '@/assets/images/icons/sidenav/dashboard.svg';
import organizationsIcon from '@/assets/images/icons/sidenav/Organizations.svg';
import addOrganizationIcon from '@/assets/images/icons/sidenav/Add_Organization.svg';
import ticketsIcon from '@/assets/images/icons/sidenav/Tickets.svg';
import chatIcon from '@/assets/images/icons/sidenav/Chat.svg';
import alertsIcon from '@/assets/images/icons/sidenav/Alerts.svg';
import kodeAiIcon from '@/assets/images/icons/sidenav/Kode_AI.svg';
import logoutIcon from '@/assets/images/icons/sidenav/Logout.svg';
import moreIcon from '@/assets/images/icons/sidenav/More.svg';

library.add(faXmark);

export default {
  name: 'BottomNav',
  data() {
    return {
      showMoreSheet: false,
      // `match` lists every route name that should light up this tab, mirroring
      // the sidenav's grouping (an organization's sub-pages keep Orgs active).
      navItems: [
        { label: 'Home', route: DASHBOARD_ROUTE, icon: dashboardIcon, match: [DASHBOARD_ROUTE] },
        {
          label: 'Orgs',
          route: ORGANIZATIONS_ROUTE,
          icon: organizationsIcon,
          match: [
            ORGANIZATIONS_ROUTE,
            EDIT_ORGANIZATION_ROUTE,
            ORGANIZATION_DETAILS_ROUTE,
            ORGANIZATION_SCREENS_ROUTE,
            NEW_SCREEN_ROUTE,
            ORGANIZATION_USERS_ROUTE,
          ],
        },
        { label: 'Chat', route: CHAT_ROUTE, icon: chatIcon, match: [CHAT_ROUTE], badge: 'chat' },
        { label: 'Tickets', route: TICKETS_ROUTE, icon: ticketsIcon, match: [TICKETS_ROUTE], badge: 'tickets' },
        { label: 'More', isMore: true, icon: moreIcon, iconClass: 'm-bottomnav__img--more' },
      ],
      // Shown inside the "More" sheet. No `route` = planned, not available yet.
      moreItems: [
        {
          label: 'New Organization',
          route: NEW_ORGANIZATION_ROUTE,
          icon: addOrganizationIcon,
          match: [NEW_ORGANIZATION_ROUTE],
        },
        { label: 'Kode AI', route: KODE_AI_ROUTE, icon: kodeAiIcon, match: [KODE_AI_ROUTE] },
        { label: 'Alerts', route: null, icon: alertsIcon, match: [] },
      ],
      logoutIcon,
    };
  },
  computed: {
    account() {
      return this.$store.getters.account.data;
    },
    displayName() {
      return accountDisplayName(this.account);
    },
    initials() {
      return accountInitials(this.account);
    },
    // Tickets with unread messages — the badge on the Tickets tab.
    unreadTickets() {
      return Number(this.$store.getters.ticketUnread?.data?.tickets) || 0;
    },
    // Conversations with unread messages — the badge on the Chat tab.
    unreadChats() {
      return chatUnread.conversations;
    },
    // A page reached from the More sheet keeps More lit.
    moreIsActive() {
      return this.moreItems.some((item) => this.isActive(item));
    },
  },
  watch: {
    // Always drop the sheet once navigation completes.
    $route() {
      this.showMoreSheet = false;
    },
  },
  methods: {
    // Match on the route name rather than relying on `router-link-active`:
    // the Home tab points at "/", whose path is a prefix of every route, so
    // the built-in active class would mark Home active everywhere.
    isActive(item) {
      return (item.match || []).includes(this.$route.name);
    },
    badgeOf(item) {
      if (item.badge === 'tickets') return this.unreadTickets;
      if (item.badge === 'chat') return this.unreadChats;
      return 0;
    },
    badgeLabel(count) {
      return count > 99 ? '99+' : String(count);
    },
    openMoreSheet() {
      this.showMoreSheet = true;
    },
    closeMoreSheet() {
      this.showMoreSheet = false;
    },
    logout() {
      this.closeMoreSheet();
      this.$store.dispatch(LOGOUT, { recordSignOut: true }).then(() => {
        this.$router.go();
      });
    },
  },
};
</script>

<style scoped>
.m-bottomnav {
  display: none;
}

@media (max-width: 991.98px) {
  /* Pinned to the real viewport bottom with position:fixed so it is never tied
     to the app shell's height. Pages reserve matching space at the end of
     their scroll region (see index.css). */
  .m-bottomnav {
    position: fixed;
    left: 0;
    right: 0;
    bottom: 0;
    z-index: 900;
    display: flex;
    flex: none;
    align-items: stretch;
    justify-content: space-around;
    min-height: 64px;
    padding: 6px 4px calc(6px + env(safe-area-inset-bottom, 0px));
    background: var(--color-bg-canvas);
    border-top: 1px solid var(--color-border);
    box-shadow: 0 -2px 12px rgba(17, 24, 39, 0.05);
  }

  .m-bottomnav__item {
    flex: 1;
    display: flex;
    flex-direction: column;
    align-items: center;
    justify-content: center;
    gap: 3px;
    padding: 0;
    background: transparent;
    border: none;
    font-family: inherit;
    text-decoration: none !important;
    color: var(--color-text-muted);
    border-radius: var(--radius-md);
    cursor: pointer;
    transition: color .15s ease;
  }

  .m-bottomnav__icon {
    display: inline-flex;
    align-items: center;
    justify-content: center;
    height: 22px;
    font-size: 1.25rem;
  }

  .m-bottomnav__icon img {
    width: 21px;
    height: 21px;
    object-fit: contain;
    opacity: 0.55;
    transition: opacity .15s ease;
  }

  /* The unread count sits on the tab's icon. */
  .m-bottomnav__icon {
    position: relative;
  }

  .m-bottomnav__badge {
    position: absolute;
    top: -6px;
    left: calc(50% + 5px);
    box-shadow: 0 0 0 2px var(--color-bg-canvas);
  }

  .m-bottomnav__icon img.m-bottomnav__img--more {
    width: 26px;
    height: 26px;
    opacity: 0.82;
  }

  .m-bottomnav__label {
    font-size: 0.66rem;
    font-weight: 500;
    line-height: 1;
  }

  .m-bottomnav__item:hover {
    color: var(--color-text-secondary);
  }

  .m-bottomnav__item:hover .m-bottomnav__icon img {
    opacity: 0.85;
  }

  .m-bottomnav__item.is-active {
    color: var(--color-primary);
  }

  .m-bottomnav__item.is-active .m-bottomnav__icon img {
    opacity: 1;
  }
}

/* =========================================================================
   "MORE" BOTTOM SHEET
   A full-screen fixed overlay with the sheet anchored to the bottom edge.
   It only ever shows on ≤991.98px, where the app zoom is off (index.css).
   ========================================================================= */
.m-sheet-overlay {
  position: fixed;
  inset: 0;
  z-index: 1200;
  display: flex;
  flex-direction: column;
  justify-content: flex-end;
  width: 100%;
  min-height: 100vh;
  min-height: 100dvh;
  background: rgba(17, 24, 39, 0.45);
}

.m-sheet {
  width: 100%;
  max-height: calc(100vh - 12px);
  max-height: calc(100dvh - 12px);
  display: flex;
  flex-direction: column;
  background: var(--color-bg-canvas);
  border-top-left-radius: 22px;
  border-top-right-radius: 22px;
  box-shadow: 0 -10px 34px rgba(17, 24, 39, 0.20);
  padding-bottom: env(safe-area-inset-bottom, 0px);
  overflow: hidden;
}

.m-sheet__handle {
  width: 42px;
  height: 4px;
  margin: 10px auto 2px;
  border-radius: 999px;
  background: var(--color-border-strong, #d1d5db);
  cursor: pointer;
}

.m-sheet__header {
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 12px;
  padding: 10px 18px 14px;
  border-bottom: 1px solid var(--color-divider);
}

.m-sheet__account {
  display: flex;
  align-items: center;
  gap: 12px;
  min-width: 0;
}

.m-sheet__avatar {
  width: 40px;
  height: 40px;
  flex: none;
  display: inline-flex;
  align-items: center;
  justify-content: center;
  border-radius: 50%;
  background: var(--color-primary);
  color: var(--color-text-on-primary);
  font-size: 0.8rem;
  font-weight: 700;
}

.m-sheet__account-meta {
  display: flex;
  flex-direction: column;
  min-width: 0;
}

.m-sheet__account-name {
  color: var(--color-text-primary);
  font-size: 0.95rem;
  font-weight: 600;
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
}

.m-sheet__account-email {
  color: var(--color-text-muted);
  font-size: 0.8rem;
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
}

.m-sheet__close {
  width: 38px;
  height: 38px;
  flex: none;
  display: inline-flex;
  align-items: center;
  justify-content: center;
  border: none;
  border-radius: 50%;
  background: var(--color-bg-muted);
  color: var(--color-text-secondary);
  font-size: 1rem;
  cursor: pointer;
  transition: background-color .15s ease, color .15s ease;
}

.m-sheet__close:hover {
  background: var(--color-border);
  color: var(--color-text-primary);
}

.m-sheet__list {
  flex: 1 1 auto;
  min-height: 0;
  list-style: none;
  margin: 0;
  padding: 10px 12px;
  overflow-y: auto;
  -webkit-overflow-scrolling: touch;
  overscroll-behavior: contain;
}

.m-sheet__list li {
  list-style: none;
}

.m-sheet__item {
  display: flex;
  align-items: center;
  gap: 14px;
  width: 100%;
  padding: 14px 12px;
  border: 0;
  border-radius: var(--radius-md);
  background: transparent;
  color: var(--color-text-primary) !important;
  font: inherit;
  text-align: left;
  text-decoration: none !important;
  cursor: pointer;
  transition: background-color .15s ease, color .15s ease;
}

.m-sheet__item:hover,
.m-sheet__item.is-active {
  background: var(--color-primary-soft);
  color: var(--color-primary-hover) !important;
}

.m-sheet__item.is-disabled,
.m-sheet__item.is-disabled:hover {
  background: transparent;
  color: var(--color-text-muted) !important;
  cursor: default;
}

.m-sheet__item.is-disabled .m-sheet__icon img {
  opacity: 0.55;
}

.m-sheet__badge {
  flex: none;
  padding: 2px 8px;
  border: 1px solid var(--color-border-strong);
  border-radius: 5px;
  color: var(--color-text-muted);
  font-size: 0.6rem;
  font-weight: 600;
  letter-spacing: 0.04em;
  text-transform: uppercase;
}

.m-sheet__signout-row {
  margin-top: 10px;
  padding-top: 10px;
  border-top: 1px solid var(--color-divider);
}

.m-sheet__item--danger,
.m-sheet__item--danger:hover {
  color: #FF383C !important;
}

.m-sheet__item--danger:hover {
  background: rgba(255, 56, 60, 0.08);
}

.m-sheet__icon {
  width: 24px;
  flex: 0 0 24px;
  display: inline-flex;
  align-items: center;
  justify-content: center;
  font-size: 1.05rem;
  color: var(--color-text-secondary);
}

.m-sheet__icon img {
  width: 21px;
  height: 21px;
  object-fit: contain;
}

.m-sheet__label {
  flex: 1 1 auto;
  min-width: 0;
  font-size: 0.95rem;
  font-weight: 500;
}

/* --- Slide-up + fade transition --- */
.sheet-enter-active,
.sheet-leave-active {
  transition: opacity .25s ease;
}

.sheet-enter-from,
.sheet-leave-to {
  opacity: 0;
}

.sheet-enter-active .m-sheet,
.sheet-leave-active .m-sheet {
  transition: transform .3s cubic-bezier(.32, .72, 0, 1);
}

.sheet-enter-from .m-sheet,
.sheet-leave-to .m-sheet {
  transform: translateY(100%);
}

/* On large screens the sheet is never opened (the nav is hidden); guard
   against it lingering if the viewport is resized up while it is open. */
@media (min-width: 992px) {
  .m-sheet-overlay {
    display: none !important;
  }
}
</style>
